import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useLocation, useRoute } from "wouter";
import { z } from "zod";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { InsertTeamMedia } from "@servify/shared-schema";

import { 
  ArrowLeft, 
  Upload, 
  Users, 
  Camera,
  FileImage,
  X 
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";

// Define form schema for editing team media
const editTeamImageSchema = z.object({
  title: z.string().min(1, "Title is required"),
  departmentId: z.number().min(1, "Department is required"),
  description: z.string().optional(),
  uploadedBy: z.number().optional(),
});

type EditTeamImageForm = z.infer<typeof editTeamImageSchema>;

export default function EditTeamImage() {
  const [, setLocation] = useLocation();
  const [, params] = useRoute("/edit-team-image/:id");
  const imageId = (params as any)?.id || '';
  
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>("");
  const [dragActive, setDragActive] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const { toast } = useToast();

  // Fetch team image details
  const { data: teamImageData, isLoading: isLoadingImage } = useQuery({
    queryKey: [`/team-images/${imageId}`],
    enabled: !!imageId,
  }) as { data: { teamImage: any } | undefined; isLoading: boolean };

  // Fetch departments for the select dropdown
  const { data: departmentsData, isLoading: isLoadingDepartments } = useQuery({
    queryKey: ['/admin/departments'],
    queryFn: async () => {
      const response = await apiRequest("GET", "/admin/departments");
      return response.json();
    },
  }) as { data: any[] | undefined; isLoading: boolean };

  const form = useForm<EditTeamImageForm>({
    resolver: zodResolver(editTeamImageSchema),
    defaultValues: {
      title: "",
      description: "",
      departmentId: 0,
    },
  });

  // Set form values when team image data is loaded
  useEffect(() => {
    if (teamImageData?.teamImage) {
      const teamImage = teamImageData.teamImage;
      form.reset({
        title: teamImage.title,
        description: teamImage.description || "",
        departmentId: teamImage.departmentId,
      });
      setPreviewUrl(teamImage.mediaUrl);
    }
  }, [teamImageData, form]);

  // Firebase upload function
  const uploadFileToFirebase = async (file: File, departmentName: string): Promise<string> => {
    try {
      setIsUploading(true);
      setUploadProgress(0);

      console.log("Starting Firebase upload for team image:", file.name);
      
      // Import Firebase functions dynamically
      const { storage, ensureAuthenticated } = await import('@/lib/firebase');
      const { ref, uploadBytes, getDownloadURL } = await import('firebase/storage');
      
      // Ensure user is authenticated with Google OAuth
      await ensureAuthenticated();
      
      // Create a storage reference in team-images folder with department subfolder
      const timestamp = Date.now();
      const fileName = `team-images/${departmentName}/${timestamp}-${file.name}`;
      const storageRef = ref(storage, fileName);
      
      console.log('Uploading to Firebase Storage:', fileName);
      setUploadProgress(25);
      
      // Upload the file
      const snapshot = await uploadBytes(storageRef, file, {
        contentType: file.type
      });
      
      console.log('Upload successful, getting download URL...');
      setUploadProgress(75);
      
      // Get the download URL
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      console.log('Download URL obtained:', downloadURL);
      setUploadProgress(100);
      
      return downloadURL;
    } catch (error) {
      console.error("Firebase upload error:", error);
      
      let errorMessage = "Failed to upload image. Please try again.";
      if (error instanceof Error) {
        if (error.message.includes('auth')) {
          errorMessage = "Please sign in with Google to upload images.";
        } else {
          errorMessage = error.message;
        }
      }
      
      toast({
        title: "Upload Error",
        description: errorMessage,
        variant: "destructive",
      });
      
      throw error;
    } finally {
      setIsUploading(false);
    }
  };

  // Update team image mutation
  const updateTeamImageMutation = useMutation({
    mutationFn: async (data: EditTeamImageForm & { mediaUrl?: string }) => {
      return apiRequest('PUT', `/team-images/${imageId}`, data);
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Team image updated successfully!",
        variant: "default",
      });
      queryClient.invalidateQueries({ queryKey: ['/team-images'] });
      setLocation('/team-images');
    },
    onError: (error: Error) => {
      console.error("Error updating team image:", error);
      toast({
        title: "Error",
        description: "Failed to update team image. Please try again.",
        variant: "destructive",
      });
    }
  });

  const handleFileSelect = (file: File) => {
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewUrl(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    
    const files = e.dataTransfer.files;
    if (files?.[0]) {
      handleFileSelect(files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
  };

  const onSubmit = async (data: EditTeamImageForm) => {
    try {
      let mediaUrl = teamImageData?.teamImage?.mediaUrl;

      // If a new file is selected, upload it to Firebase
      if (selectedFile) {
        const selectedDepartment = departmentsData && Array.isArray(departmentsData) 
          ? departmentsData.find((dept: any) => dept.id === data.departmentId)
          : null;
        if (!selectedDepartment) {
          toast({
            title: "Invalid Department",
            description: "Please select a valid department",
            variant: "destructive",
          });
          return;
        }

        mediaUrl = await uploadFileToFirebase(selectedFile, selectedDepartment.name);
      }

      // Update team image record
      await updateTeamImageMutation.mutateAsync({
        ...data,
        mediaUrl,
        fileName: selectedFile?.name || teamImageData?.teamImage?.fileName,
        fileSize: selectedFile?.size || teamImageData?.teamImage?.fileSize,
      });
    } catch (error) {
      console.error("Team image update failed:", error);
    }
  };

  if (isLoadingImage || isLoadingDepartments) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-servify-purple mx-auto mb-4"></div>
            <p className="text-gray-600">Loading team image...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!teamImageData?.teamImage) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="text-center py-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Team Image Not Found</h2>
            <p className="text-gray-600 mb-6">The team image you're looking for doesn't exist or has been deleted.</p>
            <Button onClick={() => setLocation('/team-images')}>
              Back to Team Images
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Back Button */}
        <div className="mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setLocation('/team-images')}
            className="text-gray-600 hover:text-gray-900"
            data-testid="button-back-to-team-images"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Team Images
          </Button>
        </div>

        {/* Form Card */}
        <Card className="shadow-xl shadow-servify-purple/30">
          <CardHeader className="pb-4">
            <CardTitle className="text-xl text-gray-900 flex items-center">
              <Camera className="mr-3 h-6 w-6 text-servify-purple" />
              Edit Team Image
            </CardTitle>
            <p className="text-gray-600 text-sm">Update the team image details and upload a new image if needed.</p>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                {/* Basic Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">Image Details</h3>
                  
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-gray-700">Image Title</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="Enter a descriptive title for the team image"
                            className="border-gray-300 focus:border-servify-purple focus:ring-servify-purple"
                            data-testid="input-title"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-gray-700">Description (Optional)</FormLabel>
                        <FormControl>
                          <Textarea 
                            placeholder="Add a description about this team image..."
                            className="border-gray-300 focus:border-servify-purple focus:ring-servify-purple resize-none"
                            rows={3}
                            data-testid="input-description"
                            {...field}
                            value={field.value || ""}
                          />
                        </FormControl>
                        <FormDescription>
                          Provide additional context about the team or occasion.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="departmentId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-gray-700">Department</FormLabel>
                        <Select 
                          value={field.value?.toString()} 
                          onValueChange={(value) => field.onChange(parseInt(value))}
                        >
                          <FormControl>
                            <SelectTrigger 
                              className="w-full h-11 border border-gray-300 rounded-lg bg-white hover:border-servify-purple focus:border-servify-purple focus:ring-2 focus:ring-servify-purple/20 transition-all duration-200 shadow-sm" 
                              data-testid="select-department"
                            >
                              <SelectValue placeholder="Select a department" className="text-gray-700" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent className="bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto z-50">
                            {departmentsData && Array.isArray(departmentsData) && departmentsData.map((department: any) => (
                              <SelectItem 
                                key={department.id} 
                                value={department.id.toString()}
                                className="px-3 py-2 text-sm text-gray-700 hover:bg-servify-purple/10 hover:text-servify-purple focus:bg-servify-purple/10 focus:text-servify-purple cursor-pointer transition-colors"
                              >
                                {department.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormDescription>
                          Select the department this team image belongs to.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Image Upload Section */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
                    Update Image (Optional)
                  </h3>
                  
                  {/* Current Image Preview */}
                  {previewUrl && (
                    <div className="mb-4">
                      <Label className="text-sm font-medium text-gray-700 mb-2 block">
                        {selectedFile ? "New Image Preview" : "Current Image"}
                      </Label>
                      <div className="relative w-full max-w-md">
                        <img
                          src={previewUrl}
                          alt="Preview"
                          className="w-full h-48 object-cover rounded-lg border-2 border-gray-200"
                        />
                        {selectedFile && (
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedFile(null);
                              setPreviewUrl(teamImageData?.teamImage?.mediaUrl || "");
                            }}
                            className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* File Upload Area */}
                  <div
                    className={`relative border-2 border-dashed rounded-lg p-6 transition-colors ${
                      dragActive
                        ? 'border-servify-purple bg-servify-purple/5'
                        : 'border-gray-300 hover:border-servify-purple'
                    }`}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    data-testid="upload-area"
                  >
                    <div className="text-center">
                      <FileImage className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                      <div className="flex text-sm text-gray-600">
                        <label
                          htmlFor="file-upload"
                          className="relative cursor-pointer bg-white rounded-md font-medium text-servify-purple hover:text-servify-purple/80 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-servify-purple"
                        >
                          <span>Upload a new image</span>
                          <input
                            id="file-upload"
                            name="file-upload"
                            type="file"
                            accept="image/*"
                            className="sr-only"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) handleFileSelect(file);
                            }}
                            data-testid="input-file"
                          />
                        </label>
                        <p className="pl-1">or drag and drop</p>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">PNG, JPG, GIF up to 10MB</p>
                      
                      {selectedFile && (
                        <div className="mt-3 text-sm text-servify-purple">
                          Selected: {selectedFile.name}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setLocation('/team-images')}
                    data-testid="button-cancel"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-servify-purple hover:bg-servify-purple/90 text-white px-8"
                    disabled={updateTeamImageMutation.isPending}
                    data-testid="button-update"
                  >
                    {updateTeamImageMutation.isPending ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Updating...
                      </>
                    ) : (
                      <>
                        <Upload className="mr-2 h-4 w-4" />
                        Update Team Image
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}